<template>
  <ReducedFareValidation>
    <template #summary>
      <p>Enter your work email address to verify your eligibility for a Bike Share for Business pass.</p>
    </template>
    <template #eligibility-fields>
      <!-- Email input field -->
      <o-field
        v-bind="emailErrorProps.fieldProps"
      >
        <o-input
          v-bind="emailErrorProps.inputProps"
          v-model="email"
          type="email"
          placeholder="you@company.com"
          required
          autofocus
          @blur="emailErrorProps.inputProps.onBlur"
        />
      </o-field>

      <!-- Send Code Button -->
      <div
        v-if="!verificationStatus.confirmed.success && !verificationStatus.requested.codeSent && !verificationStatus.requested.sendingCode"
        class="business-validation__actions"
      >
        <o-button
          variant="primary"
          :disabled="!email || verificationStatus.requested.sendingCode"
          @click="sendVerificationCode"
        >
          Send Verification Code
        </o-button>
      </div>

      <!-- Verification Code Input -->
      <Transition name="slide-fade">
        <div v-show="verificationStatus.requested.codeSent && !isVerificationComplete">
          <o-field
            v-bind="codeErrorProps.fieldProps"
          >
            <o-input
              v-bind="codeErrorProps.inputProps"
              v-model="verificationCode"
              type="text"
              placeholder="Enter 6-digit code"
              maxlength="6"
              pattern="[0-9]*"
              inputmode="numeric"
              required
              :disabled="verificationStatus.confirmed.loading"
              @blur="codeErrorProps.inputProps.onBlur"
            />
          </o-field>

          <!-- Resend Code Button -->
          <Transition name="slide-fade">
            <p
              v-if="!verificationStatus.requested.sendingCode"
              class="business-validation__resend"
            >
              <button
                type="button"
                class="button-link"
                @click="sendVerificationCode"
              >
                Resend Code
              </button>
            </p>
          </Transition>
        </div>
      </Transition>

      <!-- Success Message -->
      <Transition name="slide-fade">
        <p
          v-if="isVerificationComplete"
          class="business-validation__success"
        >
          ✓ Email verified! Click "Next" to continue.
        </p>
      </Transition>
    </template>
    <template #details>
      <p>
        Bike Share for Business provides discounted passes to employees of participating organizations.
        Enter your work email address to verify your eligibility.
      </p>
      <p v-if="verificationStatus.requested.codeSent && !canSkipVerification">
        A verification code has been sent to <strong>{{ email }}</strong>.
        Please check your email and enter the code above.
      </p>
      <p>
        For any questions, please call or text our help desk at
        <a href="tel:+18448572453">(844) 857-2453</a> or email
        <a href="mailto:support@bikeshare.metro.net">support@bikeshare.metro.net</a>.
      </p>
    </template>
  </ReducedFareValidation>
</template>

<script setup>
import { ref, computed, onUnmounted, inject, watch } from "vue";
import { useAccount } from "../../../../stores/account";
import ReducedFareValidation from "../ReducedFareValidationTemplate.vue";
import { useFieldErrorProvider } from "../../../FieldErrorProvider/useFieldErrorProvider";

const account = useAccount();

const email = ref(account.cart.discountVerificationForm.businessEmail || "");
const verificationCode = ref("");
const emailApiError = ref(null);
const codeApiError = ref(null);

const verificationStatus = computed(() => account.businessEmailVerification);

const canSkipVerification = computed(() => {
  return verificationStatus.value.requested?.emailValidationRequired === false &&
         verificationStatus.value.requested.validationSuccess;
});

const isVerificationComplete = computed(() => {
  return verificationStatus.value.confirmed.success;
});

const emailErrorProps = useFieldErrorProvider(
  () => email.value,
  "businessEmail",
  { apiError: emailApiError },
);

const codeErrorProps = useFieldErrorProvider(
  () => verificationCode.value,
  "businessVerificationCode",
  { apiError: codeApiError },
);

const { doFieldsHaveErrors } = inject("accountUpdateError");

// Watch for email changes to cancel verification
watch(email, (newEmail, oldEmail) => {
  if (oldEmail && newEmail !== oldEmail) {
    account.cancelBusinessEmailVerification({ cancelRequested: true });
    verificationCode.value = "";
  }
});

// Auto-submit when 6 digits entered
watch(verificationCode, (newCode) => {
  if (newCode.length === 6) {
    verifyCode();
  }
});

const sendVerificationCode = async () => {
  if (!email.value) return;

  console.log("[BusinessValidation] Sending code to:", email.value);

  try {
    // Clear any previous email errors
    emailApiError.value = null;

    await account.requestBusinessEmailVerification({ email: email.value });

    console.log("[BusinessValidation] Verification status after request:", verificationStatus.value);

    // If there's an error, update the email error props
    if (verificationStatus.value.requested.error) {
      const error = verificationStatus.value.requested.error;
      let errorMessage = error;

      if (error === "EMAIL_IN_USE") {
        errorMessage = "This email address is already associated with another account.";
      } else if (error === "DISALLOWED_EMAIL_DOMAIN") {
        errorMessage = "Please enter an email address from an approved domain to sign up for this pass.";
      } else if (error === "UNABLE_TO_SEND") {
        errorMessage = "Unable to send verification code. Please try again or contact Customer Service.";
      }

      // Set the error in the API error ref
      emailApiError.value = errorMessage;
      // Mark field as blurred to show the error message
      emailErrorProps.blurred = true;
      console.error(errorMessage);
    }
  } catch (error) {
    console.error("Error sending verification code:", error);
  }
};

const verifyCode = async () => {
  if (!verificationCode.value || verificationCode.value.length !== 6) return;

  try {
    // Clear any previous code errors
    codeApiError.value = null;

    await account.verifyBusinessEmail({
      code: verificationCode.value,
      email: email.value,
    });

    if (verificationStatus.value.confirmed.error) {
      const error = verificationStatus.value.confirmed.error;
      let errorMessage = error;

      if (error === "INVALID_CODE") {
        errorMessage = "Invalid verification code. Please try again.";
      } else if (error === "CODE_EXPIRED") {
        errorMessage = "Verification code has expired. Please request a new code.";
      }

      // Set the error in the API error ref
      codeApiError.value = errorMessage;
      console.error("Verification error:", errorMessage);
    }
  } catch (error) {
    console.error("Error verifying code:", error);
  }
};

onUnmounted(() => {
  emailErrorProps.clearErrors();
  codeErrorProps.clearErrors();
});

defineExpose({
  validate: async () => {
    // If verification is successful, proceed
    if (verificationStatus.value.confirmed.success || canSkipVerification.value) {
      // Clear any errors
      emailApiError.value = null;
      codeApiError.value = null;
      
      account.cart.discountVerificationForm.businessEmail = email.value;
      await account.validateDiscount();
      return;
    }

    // If code hasn't been sent yet, validate email and send it
    if (!verificationStatus.value.requested.codeSent) {
      if (doFieldsHaveErrors(["businessEmail"])) {
        throw new Error("Please enter a valid work email address");
      }
      await sendVerificationCode();
      
      if (verificationStatus.value.requested.error) {
        throw new Error("Please correct the email address and try again");
      }
      throw new Error("Verification code has been sent to your email. Please enter it to continue.");
    }

    // If code has been sent but not verified, verify it
    if (verificationStatus.value.requested.codeSent && !verificationStatus.value.confirmed.success) {
      if (doFieldsHaveErrors(["businessVerificationCode"])) {
        throw new Error("Please enter the verification code sent to your email");
      }
      await verifyCode();

      if (verificationStatus.value.confirmed.error) {
        throw new Error("Verification failed. Please try again.");
      }

      if (!verificationStatus.value.confirmed.success) {
        throw new Error("Verification failed. Please try again.");
      }

      account.cart.discountVerificationForm.businessEmail = email.value;
      await account.validateDiscount();
    }
  },
});
</script>

<style lang="scss" scoped>
.business-validation__actions {
  margin-top: 1rem;
}

.business-validation__resend {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.business-validation__success {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 0.25rem;
  color: #155724;
}

.button-link {
  background: none;
  border: none;
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: inherit;

  &:hover {
    color: #0056b3;
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
